import { Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { IconButton, SkeletonText } from 'utoniq-ui'

import { EventListFragment, Maybe } from '@/clients/utoniq-core/schema'
import { BaseLayout, SideMenu, SideMenuProps } from '@/components/layouts'
import { EventCard, EventCardSkeleton } from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { StandAloneThemeProvider } from '@/components/styles'
import { config } from '@/config'
import { useTranslation } from '@/core'
import { Cell, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  events: Maybe<{ edges: { node: EventListFragment }[] }>
  loading: boolean
  hasNextPage: boolean
  followArtists: Maybe<SideMenuProps['followArtists']>
  memberArtists: Maybe<SideMenuProps['memberArtists']>
  user: Maybe<SideMenuProps['user']>
  isArtist: Maybe<SideMenuProps['isArtist']>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
}

export const EventsTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
}) => {
  const { t } = useTranslation('events')
  const [openSideMenu, setOpenSideMenu] = useState<boolean>(false)

  return (
    <StandAloneThemeProvider>
      <BaseLayout
        margin
        header={{
          title: t('title'),
          left: config.applicationType === 'organization' && (
            <IconButton.AvatarNoImage
              edge="start"
              onClick={() => setOpenSideMenu(!openSideMenu)}
              bgcolor="grey.50"
              badge={
                state.user?.hasManagementTask ||
                !!state.user?.unreadNotificationCount ||
                !!state.user?.isMembershipPersonalInformationRequired
              }
            />
          ),
        }}
        navigation={{
          type: config.applicationType === 'standalone' ? 'artist' : 'global',
          props: {
            hasManagementTask: !!state.user?.hasManagementTask,
            unreadNotificationCount: state.user?.unreadNotificationCount,
            hasSettingTask:
              !!state.user?.isMembershipPersonalInformationRequired,
          },
        }}
        sx={{ pb: 6 }}
      >
        {!state.events?.edges?.length &&
          (state.loading ? (
            <Stack sx={{ py: 2 }}>
              <SkeletonText variant="h3" width="50%" />
            </Stack>
          ) : (
            <Typography
              variant="body1"
              align="center"
              sx={{ py: 6 }}
              color="text.secondary"
            >
              {t('emptyMessage')}
            </Typography>
          ))}

        {!state.loading && !!state.events?.edges?.length && (
          <Typography variant="h3" sx={{ py: 2 }}>
            {t('eventLabel')}
          </Typography>
        )}

        <ScrollFetcher
          nodes={state.events?.edges.map(({ node }) => (
            <EventCard event={node} key={node.id} />
          ))}
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          onFetchMore={onFetchMore}
          loadingComponent={
            <Stack spacing={5} sx={{ pt: 2 }}>
              {Array.from(Array(3).keys()).map(index => (
                <EventCardSkeleton key={index} />
              ))}
            </Stack>
          }
        />
        <SideMenu
          open={openSideMenu}
          onClose={() => setOpenSideMenu(false)}
          user={state.user}
          followArtists={state.followArtists}
          memberArtists={state.memberArtists}
          isArtist={state.isArtist}
        />
      </BaseLayout>
    </StandAloneThemeProvider>
  )
}

export const SomethingCell: Cell = () => null
